import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    Paper,
    Stack,
    Typography,
} from "@mui/material";
import HomeRoundedIcon from "@mui/icons-material/HomeRounded";
import ReplayRoundedIcon from "@mui/icons-material/ReplayRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import { isRouteErrorResponse, useNavigate, useRouteError } from "react-router-dom";

const DEFAULT_TITLE = "We couldn't load this page";
const DEFAULT_DESCRIPTION =
    "Something unexpected happened while opening this screen. You can reload the page or return to the main menu.";

const getTechnicalErrorMessage = (error: unknown) => {
    if (isRouteErrorResponse(error)) {
        if (typeof error.data === "string" && error.data) {
            return error.data;
        }

        if (typeof error.data?.message === "string" && error.data.message) {
            return error.data.message;
        }

        return error.statusText || `HTTP ${error.status}`;
    }

    if (error instanceof Error) {
        return error.message;
    }

    if (typeof error === "string" && error) {
        return error;
    }

    try {
        return JSON.stringify(error, null, 2);
    } catch {
        return "No additional technical details were provided.";
    }
};

const AppRouteErrorPage = () => {
    const navigate = useNavigate();
    const error = useRouteError();
    const technicalDetails = getTechnicalErrorMessage(error);

    return (
        <Box
            sx={{
                minHeight: "100vh",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                px: 2,
                py: 4,
                background:
                    "linear-gradient(180deg, rgba(246,248,251,1) 0%, rgba(234,239,245,1) 100%)",
            }}
        >
            <Paper
                elevation={3}
                sx={{
                    width: "100%",
                    maxWidth: 560,
                    p: { xs: 3, sm: 4 },
                    borderRadius: 3,
                }}
            >
                <Stack spacing={2.5}>
                    <Box>
                        <Typography variant="overline" color="text.secondary">
                            Unexpected error
                        </Typography>
                        <Typography variant="h4" fontWeight={700}>
                            {DEFAULT_TITLE}
                        </Typography>
                    </Box>

                    <Typography variant="body1" color="text.secondary">
                        {DEFAULT_DESCRIPTION}
                    </Typography>

                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                        <Button
                            variant="contained"
                            startIcon={<HomeRoundedIcon />}
                            onClick={() => navigate("/home/workspace", { replace: true })}
                        >
                            Back to menu
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={<ReplayRoundedIcon />}
                            onClick={() => navigate(0)}
                        >
                            Reload page
                        </Button>
                    </Stack>

                    <Accordion disableGutters elevation={0} sx={{ borderTop: "1px solid", borderColor: "divider" }}>
                        <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />}>
                            <Typography variant="body2" fontWeight={600}>
                                Technical details
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Typography
                                component="pre"
                                variant="body2"
                                sx={{
                                    m: 0,
                                    whiteSpace: "pre-wrap",
                                    wordBreak: "break-word",
                                    color: "text.secondary",
                                    fontFamily: "monospace",
                                }}
                            >
                                {technicalDetails}
                            </Typography>
                        </AccordionDetails>
                    </Accordion>
                </Stack>
            </Paper>
        </Box>
    );
};

export default AppRouteErrorPage;
